import React, { useState, useEffect } from 'react';
import authService from '../../services/authService';
import './SystemAdminDashboard.css';

const SystemAdminDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [dashboard, setDashboard] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [activity, setActivity] = useState([]);
    const [activeTab, setActiveTab] = useState('overview');
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showUsersModal, setShowUsersModal] = useState(false);
    const [companyToDelete, setCompanyToDelete] = useState(null);
    const [companyToEdit, setCompanyToEdit] = useState(null);
    const [companyUsers, setCompanyUsers] = useState([]);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [loadingUsers, setLoadingUsers] = useState(false);
    
    // Credit Usage state
    const [creditSummary, setCreditSummary] = useState(null);
    const [creditByCompany, setCreditByCompany] = useState([]);
    const [creditByMonth, setCreditByMonth] = useState([]);
    const [recentUsage, setRecentUsage] = useState([]);
    const [creditByActionType, setCreditByActionType] = useState([]);
    const [loadingCredits, setLoadingCredits] = useState(false);
    
    const emptyCompany = {
        name: '', subdomain: '', portalName: '', timezone: 'America/New_York',
        contactName: '', contactEmail: '', contactPhone: '',
        address: '', city: '', state: '', zip: '', country: '', website: '',
        adminFirstName: '', adminLastName: '', adminEmail: '', adminPhone: '', adminPassword: ''
    };

    const timezones = [
        { value: 'America/New_York', label: 'Eastern Time (ET)' },
        { value: 'America/Chicago', label: 'Central Time (CT)' },
        { value: 'America/Denver', label: 'Mountain Time (MT)' },
        { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
        { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
        { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
        { value: 'America/Phoenix', label: 'Arizona (no DST)' },
        { value: 'America/Toronto', label: 'Eastern Canada' },
        { value: 'America/Vancouver', label: 'Pacific Canada' },
        { value: 'Europe/London', label: 'London (GMT/BST)' },
        { value: 'Europe/Berlin', label: 'Central Europe (CET)' },
        { value: 'Europe/Paris', label: 'Paris (CET)' },
        { value: 'Asia/Tokyo', label: 'Japan (JST)' },
        { value: 'Asia/Shanghai', label: 'China (CST)' },
        { value: 'Asia/Kolkata', label: 'India (IST)' },
        { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
        { value: 'UTC', label: 'UTC' }
    ];

    const [newCompany, setNewCompany] = useState(emptyCompany);
    const [editCompany, setEditCompany] = useState(emptyCompany);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [sortColumn, setSortColumn] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');

    // Download Settings state
    const [showDownloadSettingsModal, setShowDownloadSettingsModal] = useState(false);
    const [downloadSettingsCompany, setDownloadSettingsCompany] = useState(null);
    const [companyDownloadSettings, setCompanyDownloadSettings] = useState(null);
    const [loadingDownloadSettings, setLoadingDownloadSettings] = useState(false);
    const [savingDownloadSettings, setSavingDownloadSettings] = useState(false);

    useEffect(() => {
        loadDashboard();
        loadCompanies();
        loadActivity();
    }, []);

    // Load credit data when Credit Usage tab is selected
    useEffect(() => {
        if (activeTab === 'credits') {
            loadCreditData();
        }
    }, [activeTab]);

    const loadDashboard = async () => {
        try {
            const response = await fetch('/api/systemadmin/dashboard', {
                headers: {
                    'Authorization': `Bearer ${authService.getToken()}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setDashboard(data);
            }
        } catch (error) {
            console.error('Failed to load dashboard:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadCompanies = async () => {
        try {
            const response = await fetch('/api/systemadmin/companies', {
                headers: {
                    'Authorization': `Bearer ${authService.getToken()}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setCompanies(data);
            }
        } catch (error) {
            console.error('Failed to load companies:', error);
        }
    };

    const loadActivity = async () => {
        try {
            const response = await fetch('/api/systemadmin/activity', {
                headers: {
                    'Authorization': `Bearer ${authService.getToken()}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setActivity(data);
            }
        } catch (error) {
            console.error('Failed to load activity:', error);
        }
    };

    const loadCreditData = async () => {
        setLoadingCredits(true);
        try {
            const [summaryRes, byCompanyRes, byMonthRes, recentRes, byTypeRes] = await Promise.all([
                fetch('/api/creditusage/summary', {
                    headers: { 'Authorization': `Bearer ${authService.getToken()}` }
                }),
                fetch('/api/creditusage/by-company?months=3', {
                    headers: { 'Authorization': `Bearer ${authService.getToken()}` }
                }),
                fetch('/api/creditusage/by-month?months=12', {
                    headers: { 'Authorization': `Bearer ${authService.getToken()}` }
                }),
                fetch('/api/creditusage/recent?limit=25', {
                    headers: { 'Authorization': `Bearer ${authService.getToken()}` }
                }),
                fetch('/api/creditusage/by-action-type?months=3', {
                    headers: { 'Authorization': `Bearer ${authService.getToken()}` }
                })
            ]);

            if (summaryRes.ok) setCreditSummary(await summaryRes.json());
            if (byCompanyRes.ok) setCreditByCompany(await byCompanyRes.json());
            if (byMonthRes.ok) setCreditByMonth(await byMonthRes.json());
            if (recentRes.ok) setRecentUsage(await recentRes.json());
            if (byTypeRes.ok) setCreditByActionType(await byTypeRes.json());
        } catch (error) {
            console.error('Failed to load credit data:', error);
        } finally {
            setLoadingCredits(false);
        }
    };

    const loadCompanyUsers = async (company) => {
        setSelectedCompany(company);
        setLoadingUsers(true);
        setShowUsersModal(true);
        try {
            const response = await fetch(`/api/companies/${company.id}/users`, {
                headers: {
                    'Authorization': `Bearer ${authService.getToken()}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setCompanyUsers(data);
            }
        } catch (error) {
            console.error('Failed to load company users:', error);
        } finally {
            setLoadingUsers(false);
        }
    };

    const toggleCompanyActive = async (companyId) => {
        try {
            const response = await fetch(`/api/systemadmin/companies/${companyId}/toggle-active`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${authService.getToken()}`
                }
            });
            if (response.ok) {
                loadCompanies();
                loadDashboard();
            }
        } catch (error) {
            console.error('Failed to toggle company:', error);
        }
    };

    const toggleMaintenanceMode = async (companyId) => {
        try {
            const response = await fetch(`/api/systemadmin/companies/${companyId}/toggle-maintenance`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${authService.getToken()}`
                }
            });
            if (response.ok) {
                loadCompanies();
            }
        } catch (error) {
            console.error('Failed to toggle maintenance:', error);
        }
    };

    const handleAddCompany = async () => {
        if (!newCompany.name.trim()) {
            alert('Company name is required');
            return;
        }
        if (!newCompany.subdomain.trim()) {
            alert('Subdomain is required — each company needs a unique subdomain to identify their site');
            return;
        }
        if (!newCompany.adminEmail.trim()) {
            alert('Administrator email is required');
            return;
        }
        if (!newCompany.adminPassword || newCompany.adminPassword.length < 8) {
            alert('Administrator password must be at least 8 characters');
            return;
        }
        setSaving(true);
        try {
            const response = await fetch('/api/companies/onboard', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${authService.getToken()}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(newCompany)
            });
            if (response.ok) {
                setShowAddModal(false);
                setNewCompany(emptyCompany);
                loadCompanies();
                loadDashboard();
            } else {
                const error = await response.json();
                alert(error.message || 'Failed to onboard customer');
            }
        } catch (error) {
            console.error('Failed to onboard customer:', error);
            alert('Failed to onboard customer');
        } finally {
            setSaving(false);
        }
    };

    const openEditModal = (company) => {
        setCompanyToEdit(company);
        setEditCompany({
            name: company.name || '',
            subdomain: company.subdomain || '',
            portalName: company.portalName || '',
            contactName: company.contactName || '',
            contactEmail: company.contactEmail || '',
            contactPhone: company.contactPhone || '',
            address: company.address || '',
            city: company.city || '',
            state: company.state || '',
            zip: company.zip || '',
            country: company.country || '',
            website: company.website || ''
        });
        setShowEditModal(true);
    };

    const isConfigSelectCompany = (company) => company && company.name?.toLowerCase() === 'configselect';

    const handleEditCompany = async () => {
        if (!editCompany.name.trim()) {
            alert('Company name is required');
            return;
        }
        if (!editCompany.subdomain.trim() && !isConfigSelectCompany(companyToEdit)) {
            alert('Subdomain is required — each company needs a unique subdomain to identify their site');
            return;
        }
        setSaving(true);
        try {
            const response = await fetch(`/api/companies/${companyToEdit.id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${authService.getToken()}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(editCompany)
            });
            if (response.ok) {
                setShowEditModal(false);
                setCompanyToEdit(null);
                setEditCompany(emptyCompany);
                loadCompanies();
                loadDashboard();
            } else {
                const error = await response.json();
                alert(error.message || 'Failed to update company');
            }
        } catch (error) {
            console.error('Failed to update company:', error);
            alert('Failed to update company');
        } finally {
            setSaving(false);
        }
    };

    const confirmDelete = (company) => {
        setCompanyToDelete(company);
        setShowDeleteModal(true);
    };

    const handleDeleteCompany = async () => {
        if (!companyToDelete) return;
        setDeleting(true);
        try {
            const response = await fetch(`/api/companies/${companyToDelete.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${authService.getToken()}`
                }
            });
            if (response.ok) {
                const result = await response.json();
                alert(result.message);
                setShowDeleteModal(false);
                setCompanyToDelete(null);
                loadCompanies();
                loadDashboard();
            } else {
                const error = await response.json();
                alert(error.message || 'Failed to delete company');
            }
        } catch (error) {
            console.error('Failed to delete company:', error);
            alert('Failed to delete company');
        } finally {
            setDeleting(false);
        }
    };

    const parseDate = (dateString) => {
        // Server returns UTC dates without Z suffix — ensure correct UTC parsing
        if (dateString && !dateString.endsWith('Z') && !dateString.includes('+')) {
            return new Date(dateString + 'Z');
        }
        return new Date(dateString);
    };

    const formatDate = (dateString) => {
        return parseDate(dateString).toLocaleDateString();
    };

    const formatDateTime = (dateString) => {
        const date = parseDate(dateString);
        return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD'
        }).format(amount);
    };

    const formatNumber = (num) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(num);
    };

    const getMonthName = (monthNum) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return months[monthNum - 1];
    };

    const getActivityIcon = (type) => {
        switch (type) {
            case 'RFQ': return '📄';
            case 'User': return '👤';
            case 'Company': return '🏢';
            default: return '📌';
        }
    };

    const getActionTypeIcon = (actionType) => {
        switch (actionType) {
            case 'configuration': return '⚙️';
            case 'drawing': return '📐';
            case 'bom_generation': return '📋';
            case 'model_download': return '💾';
            case 'parameter_extraction': return '🔧';
            default: return '📌';
        }
    };

    const getActionTypeLabel = (actionType) => {
        switch (actionType) {
            case 'configuration': return 'Configuration';
            case 'drawing': return 'Drawing';
            case 'bom_generation': return 'BOM Generation';
            case 'model_download': return 'Model Download';
            case 'parameter_extraction': return 'Parameter Extraction';
            default: return actionType;
        }
    };

    const defaultDownloadSettings = {
        modelEnabled: true,
        rfaEnabled: true,
        bomEnabled: true,
        drawingEnabled: true,
        pdfEnabled: true,
        configurationEnabled: true,
        stepEnabled: false,
        satEnabled: false,
        dwg2dEnabled: false,
        dwg3dEnabled: false
    };

    const openDownloadSettings = async (company) => {
        setDownloadSettingsCompany(company);
        setShowDownloadSettingsModal(true);
        setLoadingDownloadSettings(true);
        try {
            const response = await fetch(`/api/company/${company.id}/download-settings`, {
                headers: { 'Authorization': `Bearer ${authService.getToken()}` }
            });
            if (response.ok) {
                setCompanyDownloadSettings(await response.json());
            } else {
                setCompanyDownloadSettings({ ...defaultDownloadSettings });
            }
        } catch {
            setCompanyDownloadSettings({ ...defaultDownloadSettings });
        } finally {
            setLoadingDownloadSettings(false);
        }
    };

    const handleDownloadSettingToggle = (key) => {
        setCompanyDownloadSettings(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const saveDownloadSettings = async () => {
        if (!downloadSettingsCompany) return;
        setSavingDownloadSettings(true);
        try {
            const response = await fetch(`/api/company/${downloadSettingsCompany.id}/download-settings`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${authService.getToken()}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(companyDownloadSettings)
            });
            if (response.ok) {
                setShowDownloadSettingsModal(false);
                setDownloadSettingsCompany(null);
            } else {
                const error = await response.json();
                alert(error.message || 'Failed to save download settings');
            }
        } catch {
            alert('Failed to save download settings');
        } finally {
            setSavingDownloadSettings(false);
        }
    };

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    const getSortIndicator = (column) => {
        if (sortColumn !== column) return <span className="sort-indicator"></span>;
        return <span className="sort-indicator active">{sortDirection === 'asc' ? ' ▲' : ' ▼'}</span>;
    };

    const sortedCompanies = [...companies].sort((a, b) => {
        if (!sortColumn) return 0;
        const dir = sortDirection === 'asc' ? 1 : -1;
        switch (sortColumn) {
            case 'company':
                return dir * (a.name || '').localeCompare(b.name || '');
            case 'contact':
                return dir * (a.contactName || '').localeCompare(b.contactName || '');
            case 'subdomain':
                return dir * (a.subdomain || '').localeCompare(b.subdomain || '');
            case 'users':
                return dir * ((a.userCount || 0) - (b.userCount || 0));
            case 'rfqs':
                return dir * ((a.rfqCount || 0) - (b.rfqCount || 0));
            case 'status':
                const statusA = a.isActive ? 1 : 0;
                const statusB = b.isActive ? 1 : 0;
                return dir * (statusA - statusB);
            default:
                return 0;
        }
    });

    if (loading) return <div className="loading">Loading...</div>;

    return (
        <div className="system-admin-dashboard">
            <div className="dashboard-header">
                <h1>System Administrator Dashboard</h1>
                <p>Complete system overview and management</p>
            </div>

            <div className="dashboard-tabs">
                <button 
                    className={activeTab === 'overview' ? 'active' : ''} 
                    onClick={() => setActiveTab('overview')}
                >
                    Overview
                </button>
                <button 
                    className={activeTab === 'companies' ? 'active' : ''} 
                    onClick={() => setActiveTab('companies')}
                >
                    Companies
                </button>
                <button 
                    className={activeTab === 'credits' ? 'active' : ''} 
                    onClick={() => setActiveTab('credits')}
                >
                    Credit Usage
                </button>
                <button 
                    className={activeTab === 'activity' ? 'active' : ''} 
                    onClick={() => setActiveTab('activity')}
                >
                    Activity Log
                </button>
            </div>

            {activeTab === 'overview' && dashboard && (
                <div className="tab-content">
                    <h2 className="tab-title">System Overview</h2>
                    
                    <div className="card">
                        <h3>Statistics</h3>
                        <div className="stats-inline">
                            <div className="stat-item">
                                <span className="stat-label">TOTAL COMPANIES</span>
                                <span className="stat-value">{dashboard.stats.totalCompanies}</span>
                                <span className="stat-detail">{dashboard.stats.activeCompanies} active</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">TOTAL USERS</span>
                                <span className="stat-value">{dashboard.stats.totalUsers}</span>
                                <span className="stat-detail">{dashboard.stats.activeUsers} active</span>
                            </div>
                            <div className="stat-item">
                                <span className="stat-label">TOTAL RFQS</span>
                                <span className="stat-value">{dashboard.stats.totalRfqs}</span>
                                <span className="stat-detail">{dashboard.stats.rfqsThisMonth} this month</span>
                            </div>
                        </div>
                    </div>

                    <div className="card">
                        <h3>Recent Companies</h3>
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Company</th>
                                    <th>Subdomain</th>
                                    <th>Users</th>
                                    <th>RFQs</th>
                                    <th>Created</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dashboard.recentCompanies.map(company => (
                                    <tr key={company.id}>
                                        <td>{company.name}</td>
                                        <td>{company.subdomain ? `${company.subdomain}.configselect.com` : '-'}</td>
                                        <td>{company.userCount}</td>
                                        <td>{company.rfqCount}</td>
                                        <td>{formatDate(company.createdAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="card">
                        <h3>Most Active Companies</h3>
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Company</th>
                                    <th>RFQs</th>
                                    <th>Users</th>
                                    <th>Last Activity</th>
                                </tr>
                            </thead>
                            <tbody>
                                {dashboard.topCompaniesByActivity.map(company => (
                                    <tr key={company.id}>
                                        <td>{company.name}</td>
                                        <td>{company.rfqCount}</td>
                                        <td>{company.userCount}</td>
                                        <td>{company.lastActivity ? formatDate(company.lastActivity) : 'Never'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {activeTab === 'companies' && (
                <div className="tab-content">
                    <div className="tab-header">
                        <h2 className="tab-title">ConfigSelect Customers</h2>
                        <button className="btn-add" onClick={() => setShowAddModal(true)}>
                            + New ConfigSelect Customer
                        </button>
                    </div>
                    
                    <div className="card">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th className="sortable" onClick={() => handleSort('company')}>Company{getSortIndicator('company')}</th>
                                    <th className="sortable" onClick={() => handleSort('contact')}>Contact{getSortIndicator('contact')}</th>
                                    <th className="sortable" onClick={() => handleSort('subdomain')}>Subdomain{getSortIndicator('subdomain')}</th>
                                    <th className="sortable" onClick={() => handleSort('users')}>Users{getSortIndicator('users')}</th>
                                    <th className="sortable" onClick={() => handleSort('rfqs')}>RFQs{getSortIndicator('rfqs')}</th>
                                    <th className="sortable" onClick={() => handleSort('status')}>Status{getSortIndicator('status')}</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedCompanies.map(company => (
                                    <tr key={company.id} className={!company.isActive ? 'inactive' : ''}>
                                        <td>
                                            <div className="company-name">{company.name}</div>
                                            <div className="company-portal">{company.portalName || ''}</div>
                                        </td>
                                        <td>
                                            <div className="contact-info">
                                                {company.contactName && <div>{company.contactName}</div>}
                                                {company.contactEmail && <div className="contact-email">{company.contactEmail}</div>}
                                            </div>
                                        </td>
                                        <td>{company.subdomain ? `${company.subdomain}.configselect.com` : '-'}</td>
                                        <td>{company.userCount}</td>
                                        <td>{company.rfqCount}</td>
                                        <td>
                                            <span className={`status ${company.isActive ? 'active' : 'inactive'}`}>
                                                {company.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                            {company.maintenanceMode && (
                                                <span className="status maintenance">Maintenance</span>
                                            )}
                                        </td>
                                        <td className="actions-cell">
                                            <button className="btn-users" onClick={() => loadCompanyUsers(company)}>Users</button>
                                            <button className="btn-edit" onClick={() => openEditModal(company)}>Edit</button>
                                            <button className="btn-downloads" onClick={() => openDownloadSettings(company)}>Downloads</button>
                                            <button
                                                className={`btn-maintenance ${company.maintenanceMode ? 'active' : ''}`}
                                                onClick={() => toggleMaintenanceMode(company.id)}
                                            >
                                                {company.maintenanceMode ? 'End Maintenance' : 'Maintenance'}
                                            </button>
                                            <button className="btn-action" onClick={() => toggleCompanyActive(company.id)}>
                                                {company.isActive ? 'Deactivate' : 'Activate'}
                                            </button>
                                            <button className="btn-delete" onClick={() => confirmDelete(company)}>Delete</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {activeTab === 'credits' && (
                <div className="tab-content">
                    <h2 className="tab-title">Credit Usage & Costs</h2>
                    
                    {loadingCredits ? (
                        <div className="loading">Loading credit data...</div>
                    ) : (
                        <>
                            {creditSummary && (
                                <div className="card">
                                    <h3>Summary</h3>
                                    <div className="stats-inline">
                                        <div className="stat-item">
                                            <span className="stat-label">TOTAL CREDITS USED</span>
                                            <span className="stat-value">{formatNumber(creditSummary.totalCreditsUsed)}</span>
                                            <span className="stat-detail">{formatCurrency(creditSummary.totalEstimatedCost)} total cost</span>
                                        </div>
                                        <div className="stat-item">
                                            <span className="stat-label">THIS MONTH</span>
                                            <span className="stat-value">{formatNumber(creditSummary.creditsThisMonth)}</span>
                                            <span className="stat-detail">{formatCurrency(creditSummary.costThisMonth)} estimated</span>
                                        </div>
                                        <div className="stat-item">
                                            <span className="stat-label">LAST MONTH</span>
                                            <span className="stat-value">{formatNumber(creditSummary.creditsLastMonth)}</span>
                                            <span className="stat-detail">{formatCurrency(creditSummary.costLastMonth)} estimated</span>
                                        </div>
                                        <div className="stat-item">
                                            <span className="stat-label">TOTAL CONFIGURATIONS</span>
                                            <span className="stat-value">{creditSummary.totalConfigurations}</span>
                                            <span className="stat-detail">{creditSummary.configurationsThisMonth} this month</span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="card">
                                <h3>Usage by Company (Last 3 Months)</h3>
                                {creditByCompany.length === 0 ? (
                                    <div className="no-data">No credit usage recorded yet.</div>
                                ) : (
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>Company</th>
                                                <th>Configurations</th>
                                                <th>Drawings</th>
                                                <th>Total Actions</th>
                                                <th>Credits Used</th>
                                                <th>Est. Cost</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {creditByCompany.map((item, index) => (
                                                <tr key={index}>
                                                    <td>{item.companyName}</td>
                                                    <td>{item.configurationCount}</td>
                                                    <td>{item.drawingCount}</td>
                                                    <td>{item.totalActions}</td>
                                                    <td>{formatNumber(item.totalCredits)}</td>
                                                    <td>{formatCurrency(item.estimatedCost)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            <div className="card">
                                <h3>Usage by Action Type (Last 3 Months)</h3>
                                {creditByActionType.length === 0 ? (
                                    <div className="no-data">No credit usage recorded yet.</div>
                                ) : (
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>Action Type</th>
                                                <th>Count</th>
                                                <th>Credits Used</th>
                                                <th>Avg Credits</th>
                                                <th>Est. Cost</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {creditByActionType.map((item, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <span className="action-type-cell">
                                                            {getActionTypeIcon(item.actionType)} {getActionTypeLabel(item.actionType)}
                                                        </span>
                                                    </td>
                                                    <td>{item.count}</td>
                                                    <td>{formatNumber(item.totalCredits)}</td>
                                                    <td>{formatNumber(item.averageCredits)}</td>
                                                    <td>{formatCurrency(item.estimatedCost)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            <div className="card">
                                <h3>Monthly Trend (Last 12 Months)</h3>
                                {creditByMonth.length === 0 ? (
                                    <div className="no-data">No credit usage recorded yet.</div>
                                ) : (
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>Month</th>
                                                <th>Configurations</th>
                                                <th>Total Actions</th>
                                                <th>Credits Used</th>
                                                <th>Est. Cost</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {creditByMonth.map((item, index) => (
                                                <tr key={index}>
                                                    <td>{getMonthName(item.month)} {item.year}</td>
                                                    <td>{item.configurationCount}</td>
                                                    <td>{item.totalActions}</td>
                                                    <td>{formatNumber(item.totalCredits)}</td>
                                                    <td>{formatCurrency(item.estimatedCost)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            <div className="card">
                                <h3>Recent Credit Usage</h3>
                                {recentUsage.length === 0 ? (
                                    <div className="no-data">No credit usage recorded yet.</div>
                                ) : (
                                    <table className="data-table">
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th>User</th>
                                                <th>Company</th>
                                                <th>Product</th>
                                                <th>Action</th>
                                                <th>Credits</th>
                                                <th>Cost</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recentUsage.map((item) => (
                                                <tr key={item.id}>
                                                    <td>{formatDateTime(item.createdAt)}</td>
                                                    <td>{item.userName}</td>
                                                    <td>{item.companyName}</td>
                                                    <td>{item.productName || '-'}</td>
                                                    <td>
                                                        <span className="action-type-cell">
                                                            {getActionTypeIcon(item.actionType)} {getActionTypeLabel(item.actionType)}
                                                        </span>
                                                    </td>
                                                    <td>{formatNumber(item.creditsUsed)}</td>
                                                    <td>{formatCurrency(item.estimatedCost)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>

                            <div className="info-box">
                                <h3>💡 Credit Pricing Information</h3>
                                <p>
                                    <strong>Current Rate:</strong> {creditSummary ? formatCurrency(creditSummary.costPerCredit) : '$0.10'} per credit
                                </p>
                                <p>
                                    Credits are consumed when Design Automation processes configurations, generates drawings, or extracts parameters.
                                    Storage and 3D viewing are free.
                                </p>
                            </div>
                        </>
                    )}
                </div>
            )}

            {activeTab === 'activity' && (
                <div className="tab-content">
                    <h2 className="tab-title">System Activity</h2>
                    
                    <div className="card">
                        <h3>Recent Activity</h3>
                        {activity.length === 0 ? (
                            <div className="no-data">No activity recorded yet.</div>
                        ) : (
                            <div className="activity-list">
                                {activity.map((item, index) => (
                                    <div key={`${item.type}-${item.id}-${index}`} className="activity-item">
                                        <span className="activity-icon">{getActivityIcon(item.type)}</span>
                                        <div className="activity-details">
                                            <div className="activity-description">{item.description}</div>
                                            <div className="activity-meta">
                                                <span>{item.companyName}</span> • 
                                                <span> {formatDateTime(item.timestamp)}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Add ConfigSelect Customer Modal */}
            {showAddModal && (
                <div className="modal-overlay">
                    <div className="modal-content modal-large">
                        <div className="modal-header">
                            <h2>New ConfigSelect Customer</h2>
                            <button className="modal-close" onClick={() => setShowAddModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="form-section">
                                <h3>Company Information</h3>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Company Name *</label>
                                        <input
                                            type="text"
                                            value={newCompany.name}
                                            onChange={(e) => {
                                                const name = e.target.value;
                                                const subdomain = name.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
                                                setNewCompany({...newCompany, name, subdomain: newCompany.subdomain || subdomain});
                                            }}
                                            placeholder="Enter company name"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Portal Name (shown on login page)</label>
                                        <input
                                            type="text"
                                            value={newCompany.portalName}
                                            onChange={(e) => setNewCompany({...newCompany, portalName: e.target.value})}
                                            placeholder="e.g. ACME Configurator"
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Subdomain *</label>
                                        <input
                                            type="text"
                                            value={newCompany.subdomain}
                                            onChange={(e) => setNewCompany({...newCompany, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                                            placeholder="e.g. acme"
                                        />
                                        {newCompany.subdomain && (
                                            <span className="subdomain-preview">{newCompany.subdomain}.configselect.com</span>
                                        )}
                                    </div>
                                    <div className="form-group">
                                        <label>Website</label>
                                        <input
                                            type="text"
                                            value={newCompany.website}
                                            onChange={(e) => setNewCompany({...newCompany, website: e.target.value})}
                                            placeholder="https://www.example.com"
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Timezone</label>
                                        <select
                                            value={newCompany.timezone}
                                            onChange={(e) => setNewCompany({...newCompany, timezone: e.target.value})}
                                        >
                                            {timezones.map(tz => (
                                                <option key={tz.value} value={tz.value}>{tz.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group"></div>
                                </div>
                            </div>

                            <div className="form-section">
                                <h3>Company Administrator</h3>
                                <p className="section-note">This person will manage their company's users, products, and settings.</p>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>First Name *</label>
                                        <input
                                            type="text"
                                            value={newCompany.adminFirstName}
                                            onChange={(e) => setNewCompany({...newCompany, adminFirstName: e.target.value})}
                                            placeholder="First name"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Last Name *</label>
                                        <input
                                            type="text"
                                            value={newCompany.adminLastName}
                                            onChange={(e) => setNewCompany({...newCompany, adminLastName: e.target.value})}
                                            placeholder="Last name"
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Admin Email *</label>
                                        <input
                                            type="email"
                                            value={newCompany.adminEmail}
                                            onChange={(e) => setNewCompany({...newCompany, adminEmail: e.target.value})}
                                            placeholder="admin@company.com"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Admin Phone</label>
                                        <input
                                            type="text"
                                            value={newCompany.adminPhone}
                                            onChange={(e) => setNewCompany({...newCompany, adminPhone: e.target.value})}
                                            placeholder="Phone number"
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Password *</label>
                                        <input
                                            type="password"
                                            value={newCompany.adminPassword}
                                            onChange={(e) => setNewCompany({...newCompany, adminPassword: e.target.value})}
                                            placeholder="Min. 8 characters"
                                        />
                                    </div>
                                    <div className="form-group"></div>
                                </div>
                            </div>

                            <div className="form-section">
                                <h3>Primary Contact</h3>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Contact Name</label>
                                        <input
                                            type="text"
                                            value={newCompany.contactName}
                                            onChange={(e) => setNewCompany({...newCompany, contactName: e.target.value})}
                                            placeholder="Full name"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Contact Email</label>
                                        <input
                                            type="email"
                                            value={newCompany.contactEmail}
                                            onChange={(e) => setNewCompany({...newCompany, contactEmail: e.target.value})}
                                            placeholder="email@company.com"
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Contact Phone</label>
                                        <input
                                            type="text"
                                            value={newCompany.contactPhone}
                                            onChange={(e) => setNewCompany({...newCompany, contactPhone: e.target.value})}
                                            placeholder="Phone number"
                                        />
                                    </div>
                                    <div className="form-group"></div>
                                </div>
                            </div>

                            <div className="form-section">
                                <h3>Address</h3>
                                <div className="form-group">
                                    <label>Street Address</label>
                                    <input
                                        type="text"
                                        value={newCompany.address}
                                        onChange={(e) => setNewCompany({...newCompany, address: e.target.value})}
                                        placeholder="Street address"
                                    />
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>City</label>
                                        <input
                                            type="text"
                                            value={newCompany.city}
                                            onChange={(e) => setNewCompany({...newCompany, city: e.target.value})}
                                            placeholder="City"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>State/Province</label>
                                        <input
                                            type="text"
                                            value={newCompany.state}
                                            onChange={(e) => setNewCompany({...newCompany, state: e.target.value})}
                                            placeholder="State"
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>ZIP/Postal Code</label>
                                        <input
                                            type="text"
                                            value={newCompany.zip}
                                            onChange={(e) => setNewCompany({...newCompany, zip: e.target.value})}
                                            placeholder="ZIP code"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Country</label>
                                        <input
                                            type="text"
                                            value={newCompany.country}
                                            onChange={(e) => setNewCompany({...newCompany, country: e.target.value})}
                                            placeholder="Country"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={() => setShowAddModal(false)}>Cancel</button>
                            <button className="btn-save" onClick={handleAddCompany} disabled={saving}>
                                {saving ? 'Creating...' : 'Onboard Customer'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Company Modal */}
            {showEditModal && companyToEdit && (
                <div className="modal-overlay">
                    <div className="modal-content modal-large">
                        <div className="modal-header">
                            <h2>Edit Company</h2>
                            <button className="modal-close" onClick={() => setShowEditModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <div className="form-section">
                                <h3>Company Information</h3>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Company Name *</label>
                                        <input
                                            type="text"
                                            value={editCompany.name}
                                            onChange={(e) => setEditCompany({...editCompany, name: e.target.value})}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Portal Name (shown on login page)</label>
                                        <input
                                            type="text"
                                            value={editCompany.portalName}
                                            onChange={(e) => setEditCompany({...editCompany, portalName: e.target.value})}
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Subdomain {!isConfigSelectCompany(companyToEdit) ? '*' : ''}</label>
                                        <input
                                            type="text"
                                            value={editCompany.subdomain}
                                            onChange={(e) => setEditCompany({...editCompany, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                                        />
                                        {editCompany.subdomain && (
                                            <span className="subdomain-preview">{editCompany.subdomain}.configselect.com</span>
                                        )}
                                    </div>
                                    <div className="form-group">
                                        <label>Website</label>
                                        <input
                                            type="text"
                                            value={editCompany.website}
                                            onChange={(e) => setEditCompany({...editCompany, website: e.target.value})}
                                        />
                                    </div>
                                </div>
                            </div>
                            
                            <div className="form-section">
                                <h3>Primary Contact</h3>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Contact Name</label>
                                        <input
                                            type="text"
                                            value={editCompany.contactName}
                                            onChange={(e) => setEditCompany({...editCompany, contactName: e.target.value})}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Contact Email</label>
                                        <input
                                            type="email"
                                            value={editCompany.contactEmail}
                                            onChange={(e) => setEditCompany({...editCompany, contactEmail: e.target.value})}
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Contact Phone</label>
                                        <input
                                            type="text"
                                            value={editCompany.contactPhone}
                                            onChange={(e) => setEditCompany({...editCompany, contactPhone: e.target.value})}
                                        />
                                    </div>
                                    <div className="form-group"></div>
                                </div>
                            </div>
                            
                            <div className="form-section">
                                <h3>Address</h3>
                                <div className="form-group">
                                    <label>Street Address</label>
                                    <input
                                        type="text"
                                        value={editCompany.address}
                                        onChange={(e) => setEditCompany({...editCompany, address: e.target.value})}
                                    />
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>City</label>
                                        <input
                                            type="text"
                                            value={editCompany.city}
                                            onChange={(e) => setEditCompany({...editCompany, city: e.target.value})}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>State/Province</label>
                                        <input
                                            type="text"
                                            value={editCompany.state}
                                            onChange={(e) => setEditCompany({...editCompany, state: e.target.value})}
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>ZIP/Postal Code</label>
                                        <input
                                            type="text"
                                            value={editCompany.zip}
                                            onChange={(e) => setEditCompany({...editCompany, zip: e.target.value})}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Country</label>
                                        <input
                                            type="text"
                                            value={editCompany.country}
                                            onChange={(e) => setEditCompany({...editCompany, country: e.target.value})}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={() => setShowEditModal(false)}>Cancel</button>
                            <button className="btn-save" onClick={handleEditCompany} disabled={saving}>
                                {saving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Users Modal */}
            {showUsersModal && selectedCompany && (
                <div className="modal-overlay">
                    <div className="modal-content modal-large">
                        <div className="modal-header">
                            <h2>Users - {selectedCompany.name}</h2>
                            <button className="modal-close" onClick={() => setShowUsersModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            {loadingUsers ? (
                                <div className="loading">Loading users...</div>
                            ) : companyUsers.length === 0 ? (
                                <div className="no-data">No users found for this company.</div>
                            ) : (
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Email</th>
                                            <th>Phone</th>
                                            <th>Role</th>
                                            <th>Status</th>
                                            <th>Created</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {companyUsers.map(user => (
                                            <tr key={user.id} className={!user.isActive ? 'inactive' : ''}>
                                                <td>{user.firstName} {user.lastName}</td>
                                                <td>{user.email}</td>
                                                <td>{user.phone || '-'}</td>
                                                <td>{user.roleName || '-'}</td>
                                                <td>
                                                    <span className={`status ${user.isActive ? 'active' : 'inactive'}`}>
                                                        {user.isActive ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td>{formatDate(user.createdAt)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={() => setShowUsersModal(false)}>Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Modal */}
            {showDeleteModal && companyToDelete && (
                <div className="modal-overlay">
                    <div className="modal-content delete-modal">
                        <div className="modal-header delete-header">
                            <h2>⚠️ Delete Company</h2>
                            <button className="modal-close" onClick={() => setShowDeleteModal(false)}>×</button>
                        </div>
                        <div className="modal-body">
                            <p className="delete-warning">
                                Are you sure you want to delete <strong>{companyToDelete.name}</strong>?
                            </p>
                            {(companyToDelete.userCount > 0 || companyToDelete.rfqCount > 0) && (
                                <div className="delete-info">
                                    <p>This company has:</p>
                                    <ul>
                                        {companyToDelete.userCount > 0 && <li>{companyToDelete.userCount} user(s)</li>}
                                        {companyToDelete.rfqCount > 0 && <li>{companyToDelete.rfqCount} RFQ(s)</li>}
                                    </ul>
                                    <p><strong>The company will be deactivated instead of deleted.</strong></p>
                                </div>
                            )}
                            {companyToDelete.userCount === 0 && companyToDelete.rfqCount === 0 && (
                                <p className="delete-permanent">This action cannot be undone.</p>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                            <button className="btn-delete-confirm" onClick={handleDeleteCompany} disabled={deleting}>
                                {deleting ? 'Deleting...' : 'Delete Company'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Download Settings Modal */}
            {showDownloadSettingsModal && downloadSettingsCompany && (
                <div className="modal-overlay" onClick={() => setShowDownloadSettingsModal(false)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Download Settings — {downloadSettingsCompany.name}</h2>
                            <button className="modal-close" onClick={() => setShowDownloadSettingsModal(false)}>&times;</button>
                        </div>
                        <div className="modal-body">
                            {loadingDownloadSettings ? (
                                <div className="loading">Loading settings...</div>
                            ) : companyDownloadSettings ? (
                                <div className="download-settings-grid">
                                    <h4>Standard Downloads</h4>
                                    <label className="download-toggle">
                                        <input type="checkbox" checked={companyDownloadSettings.modelEnabled} onChange={() => handleDownloadSettingToggle('modelEnabled')} />
                                        <span>Model (IAM/IPT)</span>
                                    </label>
                                    <label className="download-toggle">
                                        <input type="checkbox" checked={companyDownloadSettings.rfaEnabled} onChange={() => handleDownloadSettingToggle('rfaEnabled')} />
                                        <span>RFA (Revit)</span>
                                    </label>
                                    <label className="download-toggle">
                                        <input type="checkbox" checked={companyDownloadSettings.bomEnabled} onChange={() => handleDownloadSettingToggle('bomEnabled')} />
                                        <span>BOM (CSV)</span>
                                    </label>
                                    <label className="download-toggle">
                                        <input type="checkbox" checked={companyDownloadSettings.drawingEnabled} onChange={() => handleDownloadSettingToggle('drawingEnabled')} />
                                        <span>Drawing (IDW)</span>
                                    </label>
                                    <label className="download-toggle">
                                        <input type="checkbox" checked={companyDownloadSettings.pdfEnabled} onChange={() => handleDownloadSettingToggle('pdfEnabled')} />
                                        <span>PDF</span>
                                    </label>
                                    <label className="download-toggle">
                                        <input type="checkbox" checked={companyDownloadSettings.configurationEnabled} onChange={() => handleDownloadSettingToggle('configurationEnabled')} />
                                        <span>Configuration (JSON)</span>
                                    </label>

                                    <h4>Advanced Export Formats</h4>
                                    <label className="download-toggle">
                                        <input type="checkbox" checked={companyDownloadSettings.stepEnabled} onChange={() => handleDownloadSettingToggle('stepEnabled')} />
                                        <span>STEP</span>
                                    </label>
                                    <label className="download-toggle">
                                        <input type="checkbox" checked={companyDownloadSettings.satEnabled} onChange={() => handleDownloadSettingToggle('satEnabled')} />
                                        <span>SAT</span>
                                    </label>
                                    <label className="download-toggle">
                                        <input type="checkbox" checked={companyDownloadSettings.dwg2dEnabled} onChange={() => handleDownloadSettingToggle('dwg2dEnabled')} />
                                        <span>2D DWG</span>
                                    </label>
                                    <label className="download-toggle">
                                        <input type="checkbox" checked={companyDownloadSettings.dwg3dEnabled} onChange={() => handleDownloadSettingToggle('dwg3dEnabled')} />
                                        <span>3D DWG</span>
                                    </label>
                                </div>
                            ) : null}
                        </div>
                        <div className="modal-footer">
                            <button className="btn-cancel" onClick={() => setShowDownloadSettingsModal(false)}>Cancel</button>
                            <button className="btn-save" onClick={saveDownloadSettings} disabled={savingDownloadSettings || loadingDownloadSettings}>
                                {savingDownloadSettings ? 'Saving...' : 'Save Settings'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SystemAdminDashboard;